import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const InstallPrompt = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  // Only show install prompt on dashboard
  const isAllowedPage = location.pathname === '/dashboard';

  // Check if user has previously dismissed the install prompt
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-prompt-dismissed');
    if (dismissed === 'true') {
      setHasBeenDismissed(true);
      console.log('[PWA] Install prompt was previously dismissed');
    }
  }, []);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      console.log('[PWA] App is already installed');
      return;
    }

    // Check for existing install prompt capability
    const checkInstallability = () => {
      // Check if PWA criteria are met
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasManifest = document.querySelector('link[rel="manifest"]');
      const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      
      // For iPhone Safari, always allow install since manual installation is possible
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
      
      if (hasServiceWorker && hasManifest && isHTTPS) {
        setCanInstall(true);
        console.log('[PWA] PWA criteria met - app is installable');
      } else if (isIOS && isSafari && hasManifest && isHTTPS) {
        // iPhone Safari - show install prompt even without beforeinstallprompt
        setCanInstall(true);
        console.log('[PWA] iPhone Safari detected - showing manual install option');
      } else {
        console.log('[PWA] PWA criteria not met:', { hasServiceWorker, hasManifest, isHTTPS, isIOS, isSafari });
      }
    };

    checkInstallability();

    // Only show for authenticated users on dashboard who haven't dismissed it before
    const promptTimer = setTimeout(() => {
      if (user && isAllowedPage && !hasBeenDismissed && !isInstalled && canInstall) {
        console.log('[PWA] Showing install prompt on dashboard for authenticated user');
        setShowInstallPrompt(true);
      }
    }, 5000); // Wait 5 seconds for user to get oriented

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] Before install prompt event fired');
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      setCanInstall(true);
      
      // Show our custom install prompt if user is authenticated and on dashboard
      if (user && isAllowedPage && !hasBeenDismissed) {
        setShowInstallPrompt(true);
      }
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('[PWA] App was installed successfully');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setHasBeenDismissed(true);
      localStorage.setItem('pwa-install-prompt-dismissed', 'true');
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(promptTimer);
    };
  }, [user, isAllowedPage, hasBeenDismissed, canInstall]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('[PWA] No deferred prompt available - showing install instructions');
      
      // Create a more helpful modal instead of an alert
      const installModal = document.createElement('div');
      installModal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]';
      installModal.innerHTML = `
        <div class="bg-slate-800 border border-slate-700 rounded-lg p-6 max-w-md mx-4">
          <h3 class="text-white text-lg font-semibold mb-4">Install SyntaxSocial</h3>
          <div class="text-slate-300 mb-4 space-y-2">
            <p class="font-medium">For Android (Chrome/Edge):</p>
            <ul class="list-disc list-inside text-sm space-y-1 ml-2">
              <li>Look for the install icon (⚡) in the address bar</li>
              <li>Or use Chrome menu → "Install SyntaxSocial..."</li>
            </ul>
            <p class="font-medium mt-3">For iPhone/iPad (Safari):</p>
            <ul class="list-disc list-inside text-sm space-y-1 ml-2">
              <li>Tap the Share button (square with arrow)</li>
              <li>Select "Add to Home Screen"</li>
              <li>Tap "Add" to confirm</li>
            </ul>
            <p class="font-medium mt-3">For other browsers:</p>
            <ul class="list-disc list-inside text-sm space-y-1 ml-2">
              <li>Look for "Add to Home Screen" in browser menu</li>
              <li>Or bookmark this page for quick access</li>
            </ul>
          </div>
          <div class="flex gap-2">
            <button onclick="this.parentElement.parentElement.parentElement.remove()" 
                    class="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700 transition-colors text-sm">
              Got it!
            </button>
          </div>
        </div>
      `;
      
      document.body.appendChild(installModal);
      
      // Remove modal when clicking outside
      installModal.addEventListener('click', (e) => {
        if (e.target === installModal) {
          installModal.remove();
        }
      });
      
      setShowInstallPrompt(false);
      setHasBeenDismissed(true);
      localStorage.setItem('pwa-install-prompt-dismissed', 'true');
      return;
    }

    try {
      // Show the browser's install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log(`[PWA] User choice: ${outcome}`);

      if (outcome === 'accepted') {
        console.log('[PWA] User accepted the install prompt');
      } else {
        console.log('[PWA] User dismissed the install prompt');
      }

      // Don't show the prompt again regardless of outcome
      setHasBeenDismissed(true);
      localStorage.setItem('pwa-install-prompt-dismissed', 'true');

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } catch (error) {
      console.error('[PWA] Error showing install prompt:', error);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setHasBeenDismissed(true);
    // Save dismissal state to localStorage so it doesn't show again
    localStorage.setItem('pwa-install-prompt-dismissed', 'true');
    console.log('[PWA] Install prompt dismissed permanently');
    // Don't clear deferredPrompt - user might want to install later
  };

  // Don't show anything if not authenticated, not on dashboard, already installed, or previously dismissed
  if (!user || !isAllowedPage || isInstalled || hasBeenDismissed || !canInstall) {
    return null;
  }

  return (
    <AnimatePresence>
      {showInstallPrompt && (
        <motion.div 
          className="fixed bottom-4 right-4 z-[9999] pointer-events-none"
          initial={{ 
            opacity: 0, 
            scale: 0.8, 
            y: 50,
            x: 20
          }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            y: 0,
            x: 0
          }}
          exit={{ 
            opacity: 0, 
            scale: 0.8, 
            y: 50,
            x: 20
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            opacity: { duration: 0.2 }
          }}
        >
          <div className="bg-gradient-to-r from-cyan-600 to-teal-600 rounded-lg shadow-lg p-4 max-w-sm w-full border border-cyan-500/30 pointer-events-auto">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cyan-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1">
            <h3 className="text-white font-semibold text-sm mb-1">
              Install SyntaxSocial
            </h3>
            <p className="text-cyan-100 text-xs mb-3">
              Add to your home screen for quick access and a better experience!
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleInstallClick}
                className="bg-white text-cyan-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-cyan-50 transition-colors"
              >
                Install App
              </button>
              <button
                onClick={handleDismiss}
                className="text-cyan-100 px-3 py-1.5 rounded-md text-xs hover:text-white transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-cyan-200 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt; 